import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  ApplicationSubscription,
  User
} from "../../services/workspaceAPI";
import type { CollinsUser } from "../../types/user";
import type { RootState } from "../store";

export type SessionState = {
  collinsUser?: CollinsUser;
  users: User[];
  subscriptionId: string;
};

export const initialSessionState: SessionState = {
  collinsUser: undefined,
  users: [],
  subscriptionId: ""
};

const sessionSlice = createSlice({
  name: "session",
  initialState: initialSessionState,
  reducers: {
    collinsUserChanged: (
      state,
      action: PayloadAction<CollinsUser | undefined>
    ) => {
      state.collinsUser = action.payload;
    },
    usersChanged: (state, action: PayloadAction<User[]>) => {
      state.users = action.payload;
    },
    subscriptionIdChanged: (state, action: PayloadAction<string>) => {
      state.subscriptionId = action.payload;
    }
  }
});

export const { collinsUserChanged, usersChanged, subscriptionIdChanged } =
  sessionSlice.actions;

export const selectCollinsUser = (state: RootState): CollinsUser | undefined =>
  state.session.collinsUser;
export const selectUsers = (state: RootState): User[] => state.session.users;
export const selectSubscriptionId = (state: RootState): string =>
  state.session.subscriptionId;

export const selectCurrentUser = (state: RootState): User | undefined => {
  const { users, subscriptionId } = state.session;

  return (
    users.find(
      (user) =>
        user.applicationUsers?.find(
          (applicationUser) =>
            applicationUser?.applicationSubscription?.id === subscriptionId
        ) !== undefined
    ) ?? users[0]
  );
};

export const selectSubscription = (
  state: RootState
): ApplicationSubscription | undefined => {
  const user = selectCurrentUser(state);
  const { subscriptionId } = state.session;
  if (!user) return undefined;
  if (!user.applicationUsers) return undefined;

  const applicationUser = user.applicationUsers.find(
    (item) => item?.applicationSubscription?.id === subscriptionId
  );

  return applicationUser
    ? (applicationUser.applicationSubscription as ApplicationSubscription)
    : undefined;
};

export const sessionReducer = sessionSlice.reducer;
export default sessionReducer;
